import axios from 'axios';
import { getBaseDomainFromHost } from '../utils/tenantUrl';
import {
  SESSION_EXPIRED_MESSAGE,
  setSessionExpiredFlash
} from '../utils/errorMessages';

function getApiBaseUrl() {
  if (typeof window === 'undefined') return process.env.REACT_APP_API_URL || '';
  const hostname = (window.location.hostname || '').toLowerCase();
  const base = getBaseDomainFromHost(hostname);
  const isTenantHost = base && hostname !== base && hostname.endsWith('.' + base);
  if (isTenantHost) return '';
  return process.env.REACT_APP_API_URL || '';
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Ensure credentials are sent with requests
  withCredentials: false, // Set to true if needed for CORS with credentials
});

// Request interceptor: same-origin for tenant and X-Tenant-Host; app uses /api/tenant/* for business APIs.
axiosInstance.interceptors.request.use(
  (config) => {
    config.baseURL = getApiBaseUrl();
    if (typeof window !== 'undefined' && window.location.host) {
      config.headers['X-Tenant-Host'] = window.location.host;
    }
    // Rewrite removed: all app calls use /api/tenant/* directly (except /api/auth, /api/public, /api/admin).
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const res = error.response;
    if (res && res.data) {
      const raw = res.data.error;
      if (
        raw === 'Invalid or expired token' ||
        (typeof raw === 'string' && /session has expired/i.test(raw))
      ) {
        res.data = {
          ...res.data,
          code: res.data.code || 'SESSION_EXPIRED',
          error: SESSION_EXPIRED_MESSAGE,
          userMessage: res.data.userMessage || SESSION_EXPIRED_MESSAGE
        };
      }
    }

    // Handle 401 Unauthorized - but don't interfere with login attempts
    // Only clear token and redirect if we're not on the login page
    // and the error is not from the login endpoint itself
    if (error.response?.status === 401) {
      const isLoginEndpoint = error.config?.url?.includes('/api/auth/login');
      const isLoginPage = window.location.pathname === '/login';
      
      // Don't clear token or redirect if this is a login attempt
      if (!isLoginEndpoint && !isLoginPage) {
        // Only clear and redirect if we actually had a token (means it expired/invalid)
        const hadToken = !!localStorage.getItem('token');
        if (hadToken) {
          localStorage.removeItem('token');
          setSessionExpiredFlash();
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
